import { Component, Event, EventEmitter, h, Host, Listen, Method, Prop } from '@stencil/core';
import { FeatureDialogLauncher } from '../feature-dialog-launcher/feature-dialog-launcher';
import { HTMLTiButtonElement, HTMLTiFormElementElement, InputChangeEventDetail } from '@ticom/ui-components/dist';
import { default as languageStrings } from '../../global/l10n/AddToMytiList.json';
import * as ListManagerInterfaces from './add-to-myti-list.interface';
import { fetchWithTimeout } from '@ticom/web-component-utils';
import { TranslationUtils } from '../../util/translations.util';
import { loadGlobalStyle } from '@ticom/web-component-utils/dist';

@Component({
    tag: 'ti-add-to-myti-list',
    styleUrl: './add-to-myti-list.scss',
    shadow: true
})
export class AddToMytiList {

    /**
     * Reference to the add to list button in the dialog
     */
    private _addToListButton: HTMLTiButtonElement = null;

    /**
     * App settings value to have settings based on user
     */
    private _appSettings: ListManagerInterfaces.AppSettings = null;

    /**
     * The reference to the autocomplete myti list component in dialog
     */
    private _autocompleteForMytiListsElement: HTMLTiAutocompleteForMytiListElement = null;

    /**
     * Reference to the create list component in the dialog
     */
    private _createMytiListElement: HTMLTiCreateMytiListElement = null;

    /**
     * Reference to the dialog service
     */
    private _dialogService: FeatureDialogLauncher;

    /**
     * The error context for the component
     */
    private _errorContext: ListManagerInterfaces.AddToListErrorContext = null;

    /**
     * Used to show the snackbar message
     */
    private _itemsAddedCount: number = null;

    /**
     * Store items-limit if present from prop during load so that its non-mutable prop
     */
    private _itemsLimit: number = null;

    /**
     * Tracks validation error status for the list components in addition to quantity
     */
    private _listError: boolean = true;

    /**
     * Store list-limit (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _listLimit: number = null;

    /**
     * Locale-specific set of language strings
     */
    private _localeStrings: Record<string, string>;

    /**
     * The current mode of the add to list component
     * to avoid re-renders during changes, this is private
     */
    private _mode: ListManagerInterfaces.AddToListMode = null;

    /**
     * Tracks if tags were modified
     */
    private _modifiedTags: boolean = false;

    /**
     * Specifies progress state while data is loaded
     */
    private _progressState: 'loading' | 'error' | 'ready' = null;

    /**
     * Reference to the quantity field
     */
    private _qtyFormField: HTMLTiFormElementElement = null;

    /**
     * This is the selected list for the component
     * Either from the search-lists event listener or directly from list-manager
     */
    private _selectedList: ListManagerInterfaces.ListInfo = null;

    /**
     * Store tag-limit (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _tagLimit: number = 10;

    /**
     * The reference to the upload component
     */
    private _uploadMyTiListElement: HTMLTiUploadMytiListElement = null;

    /**
     * Store user info data (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Store user lists (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _userLists: ListManagerInterfaces.ListInfo[] = null;

    /**
     * Checks for initial configuration validity
     */
    private _validConfig: boolean = true;

    /**
     * Specifies whether the add to list button shows in collapsed mode or not
     */
    @Prop() appearance: string = '';

    /**
     * Used to specify a unique context id for each instance of add to list component
     * Later used to tell apart the events coming from internal components
     * If opn prop passed and value not provided, sets itself to the opn
     */
    @Prop() contextId: string = null;

    /**
     * The current list if already available outside this component
     *
     * NOTE: List-details action bar instance will always use the exposed @Method
     * to update the private variable _currentList to have the latest changes.
     * All other points will make api calls to get the latest changes.
     */
    @Prop() currentList: ListManagerInterfaces.ListInfo = null;

    /**
     * Whether or not the add to list button is disabled
     */
    @Prop() disabled: boolean = false;

    /**
     * Base url for api calls
     */
    @Prop() domain: string;

    /**
     * Initial mode for the add to list component
     */
    @Prop() initialMode: ListManagerInterfaces.AddToListMode = 'list-search';

    /**
     * Item limit for the user
     */
    @Prop() itemsLimit: number = null;

    /**
     * Currently selected list if any
     */
    @Prop() listId: number = null;

    /**
     * List limit for new list
     */
    @Prop() listLimit: number = null;

    /**
     * Specifies the locale
     */
    @Prop() locale: string = 'en-US';

    /**
     * Specifies the orderable part number
     */
    @Prop() opn: string = null;

    /**
     * Specifies the quantity of the specific opn
     */
    @Prop() quantity: number = null;

    /**
     * Limit of opns that can be added if mode is 'list-search' or 'create-list'
     * Both inventory and limit can be enforced here
     */
    @Prop() qtyLimit: number = Number.POSITIVE_INFINITY;

    /**
     * Tag limit for new list
     */
    @Prop() tagLimit: number = null;

    /**
     * User data used to have restrictions based on AppSettings
     */
    @Prop() userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * User List Info object
     *
     * NOTE: List-details action bar instance will always use the exposed @Method
     * to update the private variable _userLists to have the latest changes.
     * All other points will make api calls to get the latest changes.
     */
    @Prop() userLists: ListManagerInterfaces.ListInfo[] = null;

    /**
     * Event emitter for success/error event of add to list edit API
     * List mode search or create doesn't fetch the new list and doesn't emit items
     * List mode upload and autosuggest fetches the new list and emits the old/new items
     */
    @Event() myTiAddToListEvent: EventEmitter<ListManagerInterfaces.MyTiAddToListEventDetail>;

    componentWillLoad() {
        // validate mode and props
        if (!this._validateInitialization()) {
            this._validConfig = false;
            return;
        }

        // load language strings for locale - fallback to 'en-US' or an empty object
        this._localeStrings = languageStrings[this.locale] || languageStrings['en-US'] || {};

        // init config
        this._mode = this.initialMode;
        this.quantity = this.quantity ?? 1;
        this.quantity = this.quantity > this.qtyLimit ? this.qtyLimit : this.quantity;
        if (!this.contextId) {
            if (this.opn) {
                this.contextId = this.opn;
            }
            if (this.listId || this.currentList) {
                this.contextId = this.currentList ? this.currentList.listId.toString() : this.listId.toString();
            }
        }
        this._initProps();

        // Load styles for dialog content
        loadGlobalStyle('feature-components');
    }

    /**
     * Listener for create list error event
     */
    @Listen('myTiCreateListErrorEvent', { target: 'body' })
    onMyTiCreateListErrorEvent(event: CustomEvent<ListManagerInterfaces.MyTiListManagerErrorEventDetail>) {
        // check if context-id matches
        if (this.contextId !== event.detail.contextId) {
            return;
        }

        this._dialogService.updateDialogContent(this._getMyTiCreateListLoadingContent(true));
        if (event.detail.errorCode === ListManagerInterfaces.CUSTOM_ERROR_STATUS_CODE_LIST_LIMIT_REACHED) {
            this._errorContext = 'list-limit';
            this._hideQuantityField();
            console.error(event.detail);
        } else {
            this._errorContext = 'create-list-error';
            console.error(event.detail);
        }
    }

    /**
     * Listener for create list success event
     */
    @Listen('myTiCreateListSuccessEvent', { target: 'body' })
    onMyTiCreateListSuccessEvent(event: CustomEvent<ListManagerInterfaces.MyTiCreateListSuccessEventDetail>) {
        // check if context-id matches
        if (this.contextId !== event.detail.contextId) {
            return;
        }

        const createdList = event.detail.createdList;
        this._showSuccessContent(createdList);
    }

    /**
     * Method to update all lists info
     *
     * @param {ListManagerInterfaces.ListInfo[]} lists - all lists info
     */
    @Method()
    async updateAllListsInfo(lists: ListManagerInterfaces.ListInfo[]) {
        this._userLists = lists;
    }

    /**
     * Method to update current list info
     *
     * @param {ListManagerInterfaces.ListInfo} listInfo - current list info
     */
    @Method()
    async updateListDetailsInfo(listInfo: ListManagerInterfaces.ListInfo) {
        this._selectedList = listInfo;
    }

    /**
     * Method to load initial data and open main dialog
     */
    private _addToList() {
        // short circuit if disabled
        if (this.disabled) {
            return;
        }

        if (!this._dialogService) {
            const win = (typeof (window as any) !== 'undefined' ? window : {}) as any;
            if (win.com && win.com.TI && win.com.TI.FeatureDialogService) {
                this._dialogService = win.com.TI.FeatureDialogService;
            }
        }

        if (this._dialogService && this._errorContext !== 'init-error') {
            this._dialogService.openDialog({
                class: 'ti-add-to-list-dialog',
                content: this._renderProgressContent(),
                modal: true
            });
        }

        // reset certain variables including user lists on every open as others may have changed those lists
        this._progressState = 'loading';
        this._listError = true;
        this._modifiedTags = false;
        this._itemsAddedCount = 0;

        // selected list is used at init for upload and opn-search modes so don't reset it for those cases
        if (this._mode === 'create-list' || this._mode === 'list-search') {
            // List-manager instance will always use the exposed @Method
            // to update the user lists to have the latest changes.
            // All other points will make api calls to get the latest changes.
            // Hence, setting here as null, so that api calls are made every time.
            this._selectedList = null;
            this._userLists = null;

            // always reset mode to search in this case
            this._mode = 'list-search';
        }

        // initialize data
        this._initData();
    }

    /**
     * Method to build request body and operations params for edit list API
     */
    private _buildEditListParams(items: ListManagerInterfaces.ListItem[]) {
        const requestBody: ListManagerInterfaces.ListInfo = {...this._selectedList};
        requestBody.items = items;
        requestBody.modifiedBy = this._userInfo.permId.toString(); // permid of modifier
        requestBody.modifiedDate = null; // populated by service

        // service will check if item already exists and override them so we can always use add-items here
        const operations: ListManagerInterfaces.AddToListOperations[] = ['add-items'];
        // if tags were modified in search list case, then add modify tags operation else ignore tags in service
        if (this._modifiedTags) {
            operations.push('modify-tags');
        }

        return { requestBody, operations };
    }

    /**
     * Method to call the edit list API
     */
    private async _editListAction(items: ListManagerInterfaces.ListItem[]) {
        // show progress dialog while API is being made
        this._progressState = 'loading';
        this._dialogService.updateDialogContent(this._renderProgressContent());

        // build the item list and required parameters
        const editListParams = this._buildEditListParams(items);

        // the actual API implementation
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/list/' + this._selectedList.listId + '?operations=' + editListParams.operations.join()
            : 'data/add-to-myti-list/edit-list.json';

        let errorResponseStatus: number = null;
        try {
            // items will only be available for upload/autosuggest cases
            const oldItems: ListManagerInterfaces.ListItem[] = this._selectedList.items;

            // make API call
            await this._makeApiCall(apiUrl, 'PATCH', editListParams.requestBody).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in editing list');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo) => {
                this._itemsAddedCount = data.totalItemCount - this._selectedList.totalItemCount;
                this._selectedList = data;
                if (data.errors?.length) {
                    throw new Error('Error in editing items or tags');
                }
                this._showSuccessContent(this._selectedList, oldItems, editListParams.operations);
            });
        } catch (err) {
            console.error(err, errorResponseStatus);
            this._progressState = 'error';
            this.myTiAddToListEvent.emit({ contextId: this.contextId, error: true });
            this._dialogService.updateDialogContent(this._renderErrorContent());
        }
    }

    /**
     * Make api call to get the app settings
     */
    private async _getAppSettings(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/settings'
            : 'data/add-to-myti-list/app-settings.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting App Settings');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.AppSettings) => {
                this._appSettings = data;
            });
        } catch (err) {
            console.error(err, errorResponseStatus);
        }

        if (!this._listLimit) {
            this._listLimit = this._appSettings ? this._getListLimit() : ListManagerInterfaces.DEFAULT_LIST_LIMIT;
        }

        if (!this._tagLimit) {
            this._tagLimit = this._appSettings?.maxTagsPerList ?? ListManagerInterfaces.DEFAULT_TAG_LIMIT;
        }

        if (!this._itemsLimit) {
            this._itemsLimit = this._appSettings ? this._getListItemsLimit() : ListManagerInterfaces.DEFAULT_LIST_ITEMS_LIMIT;
        }
    }

    /**
     * Make api call to get the current List if mode is upload or opn-search (shouldn't be called for other modes)
     */
    private async _getCurrentListInfo(listId: number): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + `/listmodel/api/v1/ext/secured/list/${listId}`
            : 'data/add-to-myti-list/edit-list.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error(`Error in getting List with id :: ${listId}`);
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo) => {
                this._selectedList = data;
            });
        } catch (err) {
            console.error(err, errorResponseStatus);
        }
    }

    /**
     * Return the list limit from App Settings based on userType
     */
    private _getListItemsLimit(): number {
        if (this._userInfo.isCGX) {
            return this._appSettings.maxItemPerListCGX;
        } else {
            switch (this._userInfo.userType) {
                case 'TIER': return this._appSettings.maxItemPerListTIer;
                case 'DIRECT': return this._appSettings.maxItemPerListDirect;
                case 'MASSMARKET': return this._appSettings.maxItemPerListMassMarket;
                default: return ListManagerInterfaces.DEFAULT_LIST_ITEMS_LIMIT;
            }
        }
    }

    /**
     * Return the list limit from App Settings based on userType
     */
    private _getListLimit(): number {
        if (this._userInfo.isCGX) {
            return this._appSettings.maxListPerCGX;
        } else {
            switch (this._userInfo.userType) {
                case 'TIER': return this._appSettings.maxListPerTIer;
                case 'DIRECT': return this._appSettings.maxListPerDirect;
                case 'MASSMARKET': return this._appSettings.maxListPerMassMarket;
                default: return ListManagerInterfaces.DEFAULT_LIST_LIMIT;
            }
        }
    }

    /**
     * Get dialog content while loading & error, & accordingly show/hide action btn
     */
    private _getMyTiCreateListLoadingContent(showActionBtn: boolean = false) {
        return [
            <h2 slot="title">{this._localeStrings['addToListText']}</h2>,
            [
                this._getQtyFieldContent(),
                <ti-create-myti-list contextId={this.contextId}></ti-create-myti-list>
            ],
            showActionBtn && <ti-button
                slot="action"
                appearance="primary"
                onClick={() => this._onAddToListBtClicked()}
                ref={(el) => this._addToListButton = el}>
                {this._localeStrings['okBtnText']}
            </ti-button>
        ];
    }

    /**
     * Return the qty field content
     */
    private _getQtyFieldContent() {
        return (
            <ti-form-element
                ref={el => this._qtyFormField = el}
                label-text={this._localeStrings['quantityLabel']}
                required
                class="ti-add-to-myti-list-form-row">
                <ti-input
                    type="number"
                    value={this.quantity.toString()}
                    placeholder={this._localeStrings['quantityPlaceholder']}
                    onTiChange={(e) => this._onQuantityChange(e)}
                />
            </ti-form-element>
        );
    }

    /**
     * Make api call to get user info
     */
    private async _getUserInfo() {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/user'
            : 'data/add-to-myti-list/user-info.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Info');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.UserInfo) => {
                this._userInfo = data;
            });
        } catch (err) {
            console.error(err, errorResponseStatus);
            this._progressState = 'error';
            this.myTiAddToListEvent.emit({ contextId: this.contextId, error: true });
            this._dialogService.updateDialogContent(this._renderErrorContent());
        }
    }

    /**
     * Make api call to get all the list infos for the user
     */
    private async _getUserLists(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/list'
            : 'data/add-to-myti-list/all-lists.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Lists');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo[]) => {
                this._userLists = data;
            });
        } catch (err) {
            console.error(err, errorResponseStatus);
            this._progressState = 'error';
            this.myTiAddToListEvent.emit({ contextId: this.contextId, error: true });
            this._dialogService.updateDialogContent(this._renderErrorContent());
        }
    }

    /**
     * Hide the qty field when create list or edit list API is underway or when list limit is reached
     */
    private _hideQuantityField() {
        if (this._qtyFormField) {
            this._qtyFormField.style.display = 'none';
        }
    }

    /**
     * To make the required api calls and get the necessary data
     */
    private async _initData() {
        // if userInfo prop is null, then make-api call to get the info
        if (!this._userInfo) {
            await this._getUserInfo();
        }

        if (this._progressState !== 'error') {
            let promise1: Promise<void>;
            let promise2: Promise<void>;
            let promise3: Promise<void>;

            // if userLists prop is null and mode is create-list or list-search, then make the api-call to get the info
            if (!this._userLists && (this._mode === 'create-list' || this._mode === 'list-search')) {
                promise1 = this._getUserLists();
            }

            // get the current list if currentLists prop is null and mode is upload or opn-search
            if (!this._selectedList && (this._mode === 'upload' || this._mode === 'opn-search')) {
                promise2 = this._getCurrentListInfo(this.listId);
            }

            // if listLimit or tagLimit prop is null, then make the api call to get the appSettings
            if ((!this._listLimit || !this._tagLimit || !this._itemsLimit)) {
                promise3 = this._getAppSettings();
            }

            await Promise.all([promise1, promise2, promise3]);

            // if there is no error, then the component is ready to be rendered
            if (this._progressState === 'loading') {
                this._progressState = 'ready';
                this._dialogService.updateDialogContent(this._renderDialogContent());
            }
        }
    }

    /**
     * Method to get get data from props, and
     * set it to private variables to avoid data manipulation from client-side
     */
    private _initProps() {
        this._listLimit = this.listLimit;
        this._tagLimit = this.tagLimit;
        this._itemsLimit = this.itemsLimit;
        this._userInfo = this.userInfo;
        this._userLists = this.userLists;
        this._selectedList = this.currentList;
    }

    /**
     * Generic method to make all api calls
     */
    private async _makeApiCall(apiUrl: string,
        methodType: ListManagerInterfaces.HttpMethodType,
        requestBody: any,
        timeout: number = ListManagerInterfaces.LIST_API_TIMEOUT_VALUE
    ): Promise<Response> {
        const apiResponse: Response = await fetchWithTimeout(apiUrl,
            {
                method: methodType,
                body: requestBody !== null && requestBody !== undefined ? JSON.stringify(requestBody) : null,
                credentials: 'include',
                headers: {
                    'Cache-Control': 'no-store, must-revalidate',
                    'Expires': '0',
                    'Content-Type': 'application/json'
                }
            }, timeout);
        return apiResponse;
    }

    /**
     * Method to handle what happens on click of dialog action button
     */
    private async _onAddToListBtClicked() {
        if (this._errorContext === 'list-limit' || this._errorContext === 'create-list-error') {
            this._dialogService.closeDialog();
            this._errorContext = null;
            return;
        }

        this._hideQuantityField();
        let items: ListManagerInterfaces.ListItem[] = [{
            itemId: null,                               // computed in service
            listId: null,                               // should be null for create but selectedList for search
            partId: null,                               // computed in service
            partNumber: this.opn,
            quantity: this.quantity ?? 1,
            customerReferenceNumber: null,
            type: null,                                 // computed in service
            itemInfo: null                              // computed in service
        }]; // only add the specific item for search and create
        switch (this._mode) {
            case 'create-list': {
                this._createMytiListElement.createListAction(items);
                this._dialogService.updateDialogContent(this._getMyTiCreateListLoadingContent());
                break;
            }
            case 'list-search':
                items[0].listId = this._selectedList.listId;
                this._editListAction(items);
                break;
            case 'upload':
                items = await this._uploadMyTiListElement.getItemsToAdd();
                this._editListAction(items);
                break;
            case 'opn-search':
                items = await this._autocompleteForMytiListsElement.getItemsToAdd();
                this._editListAction(items);
                break;
            default: console.warn('No such mode');
        }
    }

    private _onMyTiBulkAddtoListErrorEvent(event: CustomEvent<ListManagerInterfaces.MyTiListManagerErrorEventDetail>) {
        // check if context-id matches
        if (this.contextId !== event.detail.contextId) {
            return;
        }

        // disable button
        this._addToListButton.disabled = true;
    }

    /**
     * Listener for bulk add to list (upload / autocomplete) event
     */
    private _onMyTiBulkAddtoListEvent(event: CustomEvent<ListManagerInterfaces.MyTiBulkAddtoListEventDetail>) {
        // check if context-id matches
        if (this.contextId !== event.detail.contextId) {
            return;
        }

        // toggle button disable status based on validity
        this._addToListButton.disabled = !event.detail.isValid;
    }

    /**
     * Listener for create list name validation event
     */
    private _onMyTiCreateListValidNameChangeEvent(event: CustomEvent<ListManagerInterfaces.MyTiCreateListValidNameChangeEventDetail>) {
        // check if context-id matches
        if (this.contextId !== event.detail.contextId) {
            return;
        }

        if (event.detail.isListNameValid) {
            // add validation for quantity as well
            this._addToListButton.disabled = !(this.quantity && this.quantity <= this.qtyLimit);
            this._listError = false;
        } else {
            this._addToListButton.disabled = true;
            this._listError = true;
        }
    }

    /**
     * Listener for list name action event
     */
    private _onMyTiListNameActionBtnEvent(event: CustomEvent<ListManagerInterfaces.MyTiListManagerBasicEventDetail>) {
        // check if context-id matches
        if (this.contextId !== event.detail.contextId) {
            return;
        }

        // only applicable for list-search and create-list modes
        if (this._mode === 'list-search') {
            this._mode = 'create-list';
        } else if (this._mode === 'create-list') {
            this._mode = 'list-search';
        }
        this._addToListButton.disabled = true;
        this._listError = true;
        this._dialogService.updateDialogContent(this._renderDialogContent());
    }

    private _onMyTiSearchListErrorEvent(event: CustomEvent<ListManagerInterfaces.MyTiListManagerBasicEventDetail>) {
        // check if context-id matches
        if (this.contextId !== event.detail.contextId) {
            return;
        }

        this._addToListButton.disabled = true;
        this._listError = true;
    }

    /**
     * Listener for search list selected event
     */
    private _onMyTiSearchListSelectedEvent(event: CustomEvent<ListManagerInterfaces.MyTiSearchListSelectedEventDetail>) {
        // check if context-id matches
        if (this.contextId !== event.detail.contextId) {
            return;
        }

        this._selectedList = event.detail.selectedList;
        this._modifiedTags = event.detail.modifiedTags;

        // add validation for quantity as well
        this._addToListButton.disabled = !(this.quantity && this.quantity <= this.qtyLimit);
        this._listError = false;
    }

    /**
     * Listener for quantity input update
     */
    private _onQuantityChange(event: CustomEvent<InputChangeEventDetail>) {
        this.quantity = Number(event.detail.value);

        if (!this.quantity || this.quantity > this.qtyLimit) {
            this._addToListButton.disabled = true;
            this._qtyFormField.error = true;
            this._qtyFormField.errorText = this._localeStrings['quantityLimitError'].replace('{}', this.qtyLimit.toString());
        } else {
            this._addToListButton.disabled = this._listError;
            this._qtyFormField.error = false;
            this._qtyFormField.errorText = null;
        }
    }

    /**
     * Method to update mode based on tab selection
     */
    private _onTiTabSelectionChange(event: CustomEvent<{ tabId: string, expanded?: boolean }>) {
        // expanded will only come in event detail in TiTabPanelExpanded event & not TiTabSelectionChanged
        // for non-desktop mode, it will enter in this flow
        if (event.detail?.expanded !== undefined) {
            // if the tab is expanded, then we set the mode to that one
            // and unless other tab is opened, the mode will be set to that only
            if (event.detail.expanded) {
                switch (event.detail.tabId) {
                    case 'first': this._mode = 'upload';
                        break;
                    case 'second': this._mode = 'opn-search';
                        break;
                    default: console.warn('no such mode');
                }
            }
        } else {
            // for dektop mode, it will be TiTabSelectionChanged event & it will enter in this block
            this._mode = event.detail?.tabId === 'second' ? 'opn-search' : 'upload';
        }
    }

    /**
     * Method to render tabs and upload + autocomplete component
     */
    private _renderBulkAddToListContent() {
        return (
            <ti-tab-container selectedTabId={this._mode === 'upload' ? 'first' : 'second'}
                onTiTabSelectionChange={ev => this._onTiTabSelectionChange(ev)}>
                <ti-tab-panel tabId="first" tabTitle={this._localeStrings['uploadFileTabText']} onTiTabPanelExpanded={ev => this._onTiTabSelectionChange(ev)}>
                    <ti-upload-myti-list
                        ref={el => this._uploadMyTiListElement = el}
                        contextId={this.contextId}
                        domain={this.domain}
                        currentList={this._selectedList}
                        locale={this.locale}
                        itemsLimit={this._itemsLimit}
                        userInfo={this._userInfo}
                        onMyTiBulkAddtoListEvent={ev => this._onMyTiBulkAddtoListEvent(ev)}
                        onMyTiUploadListErrorEvent={ev => this._onMyTiBulkAddtoListErrorEvent(ev)}>
                    </ti-upload-myti-list>
                </ti-tab-panel>
                <ti-tab-panel tabId="second" tabTitle={this._localeStrings['autocompleteTabText']} onTiTabPanelExpanded={ev => this._onTiTabSelectionChange(ev)}>
                    <ti-autocomplete-for-myti-list
                        ref={el => this._autocompleteForMytiListsElement = el}
                        contextId={this.contextId}
                        domain={this.domain}
                        currentList={this._selectedList}
                        locale={this.locale}
                        itemsLimit={this._itemsLimit}
                        userInfo={this._userInfo}
                        onMyTiBulkAddtoListEvent={ev => this._onMyTiBulkAddtoListEvent(ev)}
                        onMyTiAutosuggestErrorEvent={ev => this._onMyTiBulkAddtoListErrorEvent(ev)}>
                    </ti-autocomplete-for-myti-list>
                </ti-tab-panel>
            </ti-tab-container>
        );
    }

    /**
     * Method to render the create list component
     */
    private _renderCreateListContent() {
        return (
            <ti-create-myti-list
                ref={(el) => this._createMytiListElement = el}
                contextId={this.contextId}
                domain={this.domain}
                locale={this.locale}
                listLimit={this._listLimit}
                tagLimit={this._tagLimit}
                userInfo={this._userInfo}
                userLists={this._userLists}
                listNameActionBtn
                listNameActionBtnText={this._localeStrings['cancelActionText']}
                onMyTiListNameActionBtnEvent={(e) => this._onMyTiListNameActionBtnEvent(e)}
                onMyTiCreateListValidNameChangeEvent={(e) => this._onMyTiCreateListValidNameChangeEvent(e)}>
            </ti-create-myti-list>
        );
    }

    /**
     * Method to render the main dialog content
     */
    private _renderDialogContent() {
        let mainContent = null;

        switch (this._mode) {
            case 'create-list': mainContent = [this._getQtyFieldContent(), this._renderCreateListContent()];
                break;
            case 'list-search': mainContent = [this._getQtyFieldContent(), this._renderSearchListsContent()];
                break;
            case 'upload':
            case 'opn-search': mainContent = [this._renderBulkAddToListContent()];
                break;
            default: console.warn('No such mode');
        }

        return [
            <h2 slot="title">{this._localeStrings['addToListText']}</h2>,
            mainContent,
            <ti-button
                data-lid="add to list modal"
                data-navtitle="add to list"
                slot="action"
                disabled
                onClick={() => this._onAddToListBtClicked()}
                ref={(el) => this._addToListButton = el}>
                {this._localeStrings['addToListText']}
            </ti-button>,
            <ti-button
                data-lid="add to list modal"
                data-navtitle="cancel"
                slot="action"
                appearance="secondary"
                onClick={() => this._dialogService.closeDialog()}>
                {this._localeStrings['cancelActionText']}
            </ti-button>
        ];
    }

    /**
     * Method to return content for error dialog
     */
    private _renderErrorContent() {
        return [
            <h2 slot="title">{this._localeStrings['addToListText']}</h2>,
            <p>{this._localeStrings['addToListErrorMsg']}</p>,
            <ti-button slot="action" appearance="primary"
                onClick={() => this._dialogService.closeDialog()}>
                {this._localeStrings['okBtnText']}
            </ti-button>
        ];
    }

    /**
     * Method to return content for progress dialog
     */
    private _renderProgressContent() {
        return [
            <h2 slot="title">{this._localeStrings['addToListText']}</h2>,
            <div class="ti-create-myti-list-dialog-loader-container">
                <ti-progress></ti-progress>
            </div>
        ];
    }

    /**
     * Method to render the search lists component
     */
    private _renderSearchListsContent() {
        return (
            <ti-search-myti-lists
                contextId={this.contextId}
                domain={this.domain}
                headerLabelText={this._localeStrings['addToListText']}
                locale={this.locale}
                listLimit={this._listLimit}
                tagLimit={this._tagLimit}
                userInfo={this._userInfo}
                userLists={this._userLists}
                listNameActionBtn
                listNameActionBtnText={this._localeStrings['createNewListActionText']}
                onMyTiListNameActionBtnEvent={(e) => this._onMyTiListNameActionBtnEvent(e)}
                onMyTiSearchListErrorEvent={(e) => this._onMyTiSearchListErrorEvent(e)}
                onMyTiSearchListSelectedEvent={(e) => this._onMyTiSearchListSelectedEvent(e)}>
            </ti-search-myti-lists>
        );
    }

    /**
     * Method to render add-to-list button based on condition
     */
    private _renderAddToListButton(showText: boolean) {
        return (
            <ti-button
                class={{ 'ti-add-to-myti-list-btn': !this.appearance.includes('icon-only') }}
                data-lid="add to list"
                data-navtitle="add to list"
                disabled={this.disabled}
                appearance={this.appearance}
                onClick={() => this._addToList()}
                accessibleName={!showText && this._localeStrings['addToListText']}>
                <ti-svg-icon appearance="secondary" icon-set="actions" size="s">add-to-list</ti-svg-icon>
                { showText && this._localeStrings['addToListText'] }
            </ti-button>
        );
    }

    /**
     * Method to show success content
     */
    private async _showSuccessContent(list: ListManagerInterfaces.ListInfo, oldItems: ListManagerInterfaces.ListItem[] = [], operations: ListManagerInterfaces.AddToListOperations[] = []) {
        if (this._mode === 'create-list' || this._mode === 'list-search') {
            // emit success event
            this._progressState = 'ready';
            this.myTiAddToListEvent.emit({ contextId: this.contextId, listOnSuccess: this._selectedList });

            const translationDataMap1 = new Map();
            translationDataMap1.set('{0}', this.opn);
            translationDataMap1.set('{1}', list.name);
            const msg1: string[] = TranslationUtils.deconstructComplexTranslations(
                this._localeStrings['addToListSuccessMsg1'],
                {
                    tags: ['b'],
                    elComputation: true,
                    injectData: translationDataMap1
                }
            );

            const translationDataMap2 = new Map();
            translationDataMap2.set('{0}', list.name);
            const msg2: string[] = TranslationUtils.deconstructComplexTranslations(
                this._localeStrings['addToListSuccessMsg2'],
                {
                    tags: ['a'],
                    elComputation: true,
                    injectData: translationDataMap2
                }
            );

            this._dialogService.updateDialogContent([
                <h2 slot="title">{this._localeStrings['addToListText']}</h2>,
                <p>
                    <b>{msg1[0]}</b>{msg1[1]}<b>{msg1[2]}</b>{msg1[3]}
                </p>,
                <p class="ti-add-to-myti-list-margin-bottom-0">
                    {msg2[0]}<a target="_blank" href={`/list-manager/${this.locale}/${list.listId}`}>{msg2[1]}</a>{msg2[2]}
                </p>,
                <ti-button slot="action" appearance="primary" onClick={() => this._dialogService.closeDialog()}>
                    {this._localeStrings['okBtnText']}
                </ti-button>
            ]);
        } else {
            // get list details here for items list (only populated for upload/autosuggest cases)
            const oldItemIds = oldItems?.map(item => item.itemId);
            await this._getCurrentListInfo(this._selectedList?.listId);
            const newItems = this._selectedList?.items?.filter(item => !oldItemIds.includes(item.itemId));
            this.myTiAddToListEvent.emit({
                contextId: this.contextId,
                listOnSuccess: { ...this._selectedList, items: null },
                newItems,
                oldItems,
                operations
            });
            this._progressState = 'ready';

            this._dialogService.closeDialog();
            const snackbarMessage: string = this._localeStrings['addSuccessMessage'].replace('{0}', this._itemsAddedCount?.toString());
            (window as any).com?.TI?.SnackbarService?.showImmediately(snackbarMessage);
        }
    }

    /**
     * Method to validate the initial props of the component
     */
    private _validateInitialization() {
        if (this.initialMode !== 'create-list' && this.initialMode !== 'list-search' && this.opn) {
            this._errorContext = 'init-error';
            console.error('failed initial validation: opn prop is only allowed for modes list-search and create-list.');
            return false;
        }

        if ((this.initialMode === 'create-list' || this.initialMode === 'list-search') && !this.opn) {
            this._errorContext = 'init-error';
            console.error('failed initial validation: opn prop is required for modes list-search and create-list.');
            return false;
        }

        if (!this.qtyLimit) {
            this._errorContext = 'init-error';
            console.error('failed initial validation: quantity limit must be a valid number.');
            return false;
        }

        if ((this.initialMode === 'upload' || this.initialMode === 'opn-search') && !(this.listId || this.currentList)) {
            this._errorContext = 'init-error';
            console.error('failed initial validation: listId or currentList prop is required for modes upload and open-search.');
            return false;
        }

        if (this.listId && this.currentList) {
            this._errorContext = 'init-error';
            console.error('failed initial validation: Either listId or currentList should be provided but not both.');
            return false;
        }

        if (this.initialMode !== 'create-list' && this.initialMode !== 'list-search' && !this.contextId) {
            console.warn('validation warning: modes upload and opn-search may require a unique context id for listeners');
            return true;
        }

        return true;
    }

    render() {
        if (!this._validConfig) {
            return null;
        }
        return (
            <Host>
                {this.appearance.includes('icon-only')
                    ? <ti-tooltip-trigger>
                        {this._renderAddToListButton(false)}
                        <ti-tooltip position="auto">
                            <span> {this._localeStrings['addToListTooltipText']} </span>
                        </ti-tooltip>
                    </ti-tooltip-trigger>
                    : this._renderAddToListButton(true)
                }
            </Host>
        );
    }

}
